import { paramsSerializer } from "@core/helpers"
import axios from "@core/plugins/axios.plugin"
import { handleServiceCall } from "@services/helpers"
import {
    BuildingSource,
    Source,
    WorkflowEnedis,
    Note,
    CreateSensorPayload,
    UpdateSensorPayload,
    SourceLegacy,
    SourceDapi,
    SourceListItem,
} from "@types"

// =====================
// Type definitions des réponses des services
// =====================
export type DataSourceByIdResponse = {
    /**
     * Données de la source
     */
    data: {
        date: string
    }[]
    /**
     * Liste de données de variables sur une période
     */
    periode: {
        data: object
    }
}

export type SourceByIdResponse = Source

/**
 * Source au format liste (GET /v5/source/:_id_source/v2)
 */
export type SourceByIdV2Response = BuildingSource

export type WorkflowEnedisResponseSource = WorkflowEnedis[]

export type GetSourceInformationResponse = SourceDapi[]

/**
 * Station météo retournée par GET /v5/source/meteo
 */
export type WeatherStationItem = {
    _id: string
    name: string
    idsource: string
    operator: string
    position: number[] // [lng, lat]
}

export type GetWeatherStationsResponse = WeatherStationItem[]

export type CreateNoteBySourceId = Note[]
export type DeleteNoteBySourceIdAndNoteId = Note[]
export type UpdateNoteBySourceId = Note[]
export type GetNotesBySourceId = Note[]

/**
 * Réponse de GET /api/metier/v5/source
 */
export type GetSourcesResponse = {
    /**
     * Liste des sources
     */
    data: SourceListItem[]
    /**
     * Indique s'il y a plus de pages
     */
    has_more: boolean
    /**
     * Nombre total (optionnel, présent si limit n'est pas défini)
     */
    total?: number
    /**
     * Numéro de la page suivante (optionnel)
     */
    next_page?: number | null
    /**
     * Numéro de la page précédente (optionnel)
     */
    previous_page?: number | null
}

/**
 * Réponse de GET /api/metier/v5/source/count
 */
export type GetSourcesCountResponse = {
    /**
     * Nombre total de sources
     */
    total: number
    /**
     * Nombre total après filtrage (optionnel)
     */
    total_filter?: number
    /**
     * Sources avec demande de consentement en cours (voir route count)
     */
    total_consent_pending?: number
    /**
     * Indicateurs clés (KPIs)
     */
    kpis: { type: string; value: number }[]
}

/**
 * Réponse v2 des routes sensor (POST/PUT) : data = source, meta à côté
 */
export type SensorV2Response = {
    data: SourceLegacy
    meta: { should_launch_getlastdata_task: boolean }
}

// =====================
// Service
// =====================

/**
 * Le service permettant de gérer les sources de données
 */
export class SourceService {
    // =========================== //
    // Récupération des sources
    // =========================== //
    /**
     * Récupère les informations d'une source par son identifiant
     */
    async getSourceById(_id_source: string): Promise<SourceByIdResponse> {
        return handleServiceCall<SourceByIdResponse>(async () => {
            const {
                data: { data: sources },
            } = await axios.get(`/api/dapi/source/informations`, {
                params: {
                    _id_source,
                    _variable: true,
                    _pricer: true,
                },
            })

            const source = sources?.[0] || {}

            // Ajoute les informations métier à la source
            const { data: sourceMetier } = await axios.get<BuildingSource>(
                `/api/metier/v5/source/${_id_source}`
            )

            source.rights = sourceMetier?.rights || []

            // Ajoute les informations de user dans la source
            if (["pdl", "pce"].includes(source.type)) {
                source.consentement = sourceMetier?.consentement || null
                source.consentrequest = sourceMetier?.consentrequest || null
            } else if (source.type === "iot") {
                // on remplace le type par le idtype
                source.type = sourceMetier?.idtype || "iot"
            }

            return source
        })
    }

    /**
     * Récupère les informations de sources avec l'API DAPI
     * @param sourceIds - Liste des identifiants de sources
     * @param abortController - Contrôleur d'annulation
     * @returns Les informations des sources
     */
    async getSourcesInformations(
        sourceIds: string[],
        abortController?: AbortController
    ): Promise<GetSourceInformationResponse> {
        return handleServiceCall<GetSourceInformationResponse>(async () => {
            const {
                data: { data: sources },
            } = await axios.get(`/api/dapi/source/informations`, {
                params: {
                    _id_source: sourceIds,
                    _pricer: true,
                    _variable: true,
                },
                paramsSerializer: paramsSerializer,
                signal: abortController?.signal,
            })
            return sources
        })
    }

    /**
     * Récupére les infos d'un compteur en utilisant l'API Métier. Récupère ainsi les bâtiments associés
     * @param sourceId - identifiant unique de la source
     * @returns Les informations de la source
     */
    async getSourceByIdOldVersion(sourceId: string) {
        return handleServiceCall<SourceLegacy>(async () => {
            const { data } = await axios.get(`/api/metier/source/${sourceId}`)
            return data
        })
    }
    /**
     * Récupère la liste des stations météo disponibles
     * GET /v5/source/meteo
     * @returns Liste des stations météo (Météo France, OpenMeteo, etc.)
     */
    async getWeatherStations(): Promise<GetWeatherStationsResponse> {
        return handleServiceCall<GetWeatherStationsResponse>(async () => {
            const { data } = await axios.get(`/api/metier/v5/source/meteo`)
            return data
        })
    }

    /**
     * Récupère la liste paginée des sources
     * GET /api/metier/v5/source
     * @param params - Paramètres de pagination et filtres
     * @param params.page - Numéro de page (défaut: 1)
     * @param params.limit - Nombre d'éléments par page (défaut: 20)
     * @param params.building - Inclure les informations des bâtiments (défaut: false)
     * @param params.idtype - Type(s) de source(s) à filtrer (string ou string[])
     * @param params.battery - Niveau de batterie à filtrer (string ou string[])
     * @param params.location - Localisation à filtrer (string ou string[])
     * @param params.broadcast - État de broadcast à filtrer (string ou string[])
     * @param params.etat_droit_acces - État du droit d'accès à filtrer (string ou string[])
     * @param params.rights - Droits à filtrer (string)
     * @param params.search - Recherche textuelle (string)
     * @param params.usage - Usage à filtrer (string ou string[])
     * @param params.segment - Segment à filtrer (string ou string[])
     * @returns Liste paginée des sources avec indicateur has_more
     */
    async getSources(params: {
        page?: number
        limit?: number
        building?: boolean
        idtype?: string | string[]
        battery?: string | string[]
        location?: string | string[]
        broadcast?: string | string[]
        etat_droit_acces?: string | string[]
        rights?: string | string[]
        search?: string
        usage?: string | string[]
        segment?: string | string[]
        consent_pending_only?: boolean
    }): Promise<GetSourcesResponse> {
        return handleServiceCall<GetSourcesResponse>(async () => {
            const { data } = await axios.get(`/api/metier/v5/source`, {
                params: {
                    page: params.page,
                    limit: params.limit,
                    building: params.building,
                    idtype: params.idtype,
                    battery: params.battery,
                    location: params.location,
                    broadcast: params.broadcast,
                    etat_droit_acces: params.etat_droit_acces,
                    rights: params.rights,
                    search: params.search,
                    usage: params.usage,
                    segment: params.segment,
                    consent_pending_only: params.consent_pending_only,
                },
                paramsSerializer: paramsSerializer,
            })

            return data
        })
    }

    /**
     * Récupère le nombre total de sources et les KPIs
     * GET /api/metier/v5/source/count
     * @param params - Paramètres de filtres (mêmes que getSources, sans page/limit)
     * @returns Total, total filtré (optionnel) et KPIs
     */
    async getSourcesCount(params: {
        idtype?: string | string[]
        battery?: string | string[]
        location?: string | string[]
        broadcast?: string | string[]
        etat_droit_acces?: string | string[]
        rights?: string | string[]
        search?: string
        usage?: string | string[]
        segment?: string | string[]
        consent_pending_only?: boolean
    }): Promise<GetSourcesCountResponse> {
        return handleServiceCall<GetSourcesCountResponse>(async () => {
            const { data } = await axios.get(`/api/metier/v5/source/count`, {
                params: {
                    idtype: params.idtype,
                    battery: params.battery,
                    location: params.location,
                    broadcast: params.broadcast,
                    etat_droit_acces: params.etat_droit_acces,
                    rights: params.rights,
                    search: params.search,
                    usage: params.usage,
                    segment: params.segment,
                    consent_pending_only: params.consent_pending_only,
                },
                paramsSerializer: paramsSerializer,
            })
            return data
        })
    }

    // =========================== //
    // Gestion des notes
    // =========================== //

    /**
     * Crée une note pour une source
     * @param _id_source - Identifiant de la source
     * @param note - Contenu de la note
     * @returns La note créée
     */
    async createNoteBySourceId(_id_source: string, note: string) {
        return handleServiceCall<CreateNoteBySourceId>(async () => {
            const {
                data: { data },
            } = await axios.post(`/api/metier/source/${_id_source}/note`, {
                note,
            })
            return data
        })
    }

    /**
     * Récupère les notes d'une source
     * @param _id_source - Identifiant de la source
     * @returns La liste des notes
     */
    async getNotesBySourceId(_id_source: string) {
        return handleServiceCall<GetNotesBySourceId>(async () => {
            const {
                data: { data },
            } = await axios.get(`/api/metier/source/${_id_source}/note`)
            return data
        })
    }

    /**
     * Supprime une note d'une source
     * @param _id_source - Identifiant de la source
     * @param _id_note - Identifiant de la note
     */
    async deleteNoteBySourceIdAndNoteId(_id_source: string, _id_note: string) {
        return handleServiceCall<DeleteNoteBySourceIdAndNoteId>(async () => {
            const {
                data: { data },
            } = await axios.delete(
                `/api/metier/source/${_id_source}/note/${_id_note}`
            )
            return data
        })
    }

    /**
     * Met à jour une note d'une source
     * @param _id_source - Identifiant de la source
     * @param _id_note - Identifiant de la note
     * @param note - Contenu de la note
     * @returns La note mise à jour
     */
    async updateNoteBySourceId(
        _id_source: string,
        _id_note: string,
        note: string
    ) {
        return handleServiceCall<UpdateNoteBySourceId>(async () => {
            const { data } = await axios.put(
                `/api/metier/source/${_id_source}/note/${_id_note}`,
                {
                    note,
                }
            )
            return data
        })
    }

    // =========================== //
    // Gestion des compteurs
    // =========================== //

    /**
     * Met à jour un compteur
     * @param sourceId - identifiant unique de la source
     * @param source - information de la source à mettre à jour
     */
    async updateMeterById(sourceId: string, source: unknown) {
        return handleServiceCall<SourceLegacy>(async () => {
            const { data: sourceUpdated } = await axios.put(
                `/api/metier/v5/source/${sourceId}/meter`,
                source
            )
            return sourceUpdated
        })
    }

    /**
     * Déclenche une collecte on-demand des données d'une source (« Lancer une
     * récupération »). Côté api-metier, le routage source→collecteur s'applique :
     * un PCE grdf part vers harvest (DEV-527), les autres opérateurs gardent leur
     * collecteur. Renvoie le `task_id` à suivre (toast/subscribe).
     * @param sourceId - identifiant de la source
     * @param options.rebuild - true = fenêtre large (reconstruction historique)
     * @returns { status, task_id, event_name }
     */
    async triggerSourceDataUpdate(
        sourceId: string,
        { rebuild = false }: { rebuild?: boolean } = {}
    ) {
        return handleServiceCall<{
            status: string
            task_id: string
            event_name?: string
        }>(async () => {
            const { data } = await axios.post(
                `/api/metier/task/update/source`,
                {
                    _id_source: sourceId,
                    rebuild,
                    queue: "front",
                    request: {
                        tracker: { sent_at: new Date(), sent_by: "front" },
                        subscribe: true,
                    },
                }
            )
            return data
        })
    }

    // =========================== //
    // Gestion des capteurs IoT
    // =========================== //

    /**
     * Crée un capteur IoT (API v2, réponse { data, meta })
     * @param payload - Données du capteur à créer
     * @returns Réponse brute { data: source, meta: { should_launch_getlastdata_task } }
     */
    async createSensor(payload: CreateSensorPayload) {
        return handleServiceCall<SensorV2Response>(async () => {
            const { data } = await axios.post<SensorV2Response>(
                `/api/metier/v2/source/sensor`,
                payload
            )
            return data
        })
    }

    /**
     * Met à jour un capteur IoT (API v2, réponse { data, meta })
     * @param sourceId - Identifiant de la source
     * @param payload - Données du capteur à mettre à jour
     * @returns Réponse brute { data: source, meta: { should_launch_getlastdata_task } }
     */
    async updateSensor(sourceId: string, payload: UpdateSensorPayload) {
        return handleServiceCall<SensorV2Response>(async () => {
            const { data } = await axios.put<SensorV2Response>(
                `/api/metier/v2/source/sensor/${sourceId}`,
                payload
            )
            return data
        })
    }

    // =========================== //
    // Gestion des workflows Enedis
    // =========================== //

    /**
     * Récupère tous les workflows Enedis pour une source
     * @param _id_source - Identifiant du compteur
     */
    async getAllEnedisWorkflowBySourceId(_id_source: string) {
        return handleServiceCall<WorkflowEnedisResponseSource>(async () => {
            const { data } = await axios.get(
                `/api/metier/v5/workflow/data/enedis/source/${_id_source}`,
                {
                    params: {
                        limit: 10,
                        // regex pour récupérer les workflows de type update_one et historique_*
                        nameRegex: "^(update_once|historique_.*)",
                        // seulement les workflows en cours d'exécution
                        state: "run",
                    },
                }
            )
            return data
        })
    }

    // =========================== //
    // Gestion des données
    // =========================== //

    /**
     * Récupère les informations d'une source par son identifiant
     * @param _id_source - Identifiant de la source
     * @param date_from - Date de début au format YYYY-MM-DD
     * @param date_to - Date de fin au format YYYY-MM-DD
     * @param freq - Fréquence (`raw`, `hourly`, `daily`, `weekly`, `monthly`)
     * @param variables - Liste des variables à récupérer au format x_y_z.stat,
     * si non définis alors la liste est calculée selon la source.
     *
     * @returns Les données au format JSON string
     */
    async getDataSourceById(
        _id_source: string,
        date_from: string,
        date_to: string,
        freq: string,
        variables: any[] | null
    ): Promise<DataSourceByIdResponse> {
        return handleServiceCall<DataSourceByIdResponse>(async () => {
            const { data } = await axios.get(
                `/api/dapi/source/${_id_source}/data`,
                {
                    params: {
                        date_from,
                        date_to,
                        freq,
                        variables,
                    },
                    paramsSerializer: paramsSerializer,
                }
            )
            return data
        })
    }

    // =========================== //
    // Suppression de sources
    // =========================== //

    /**
     * Supprime une source par son identifiant
     * DELETE /api/metier/v5/source/:id
     * @param sourceId - Identifiant de la source à supprimer
     * @returns Promise résolue en cas de succès (204 No Content)
     */
    async deleteSourceById(sourceId: string): Promise<void> {
        return handleServiceCall<void>(async () => {
            await axios.delete(`/api/metier/v5/source/${sourceId}`)
        })
    }

    // =========================== //
    // Export de sources
    // =========================== //

    /**
     * Exporte la liste des sources au format Excel
     * GET /api/metier/source/export
     * @returns URL du fichier Excel généré
     */
    async exportSources(): Promise<string> {
        return handleServiceCall<string>(async () => {
            const { data } = await axios.get(`/api/metier/source/export`)
            return data?.url || ""
        })
    }
}
